import React, { useEffect, useState, useCallback, useContext } from "react";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { LoadingCircle } from "../../components/UI/Custom/LoadingCircle";
import { InputSelector } from "../../components/UI/InputSelector";
import { handleError } from "../../utils/custom/errorHandler";
import { Notification } from "../../components/UI/Notifications";
import { getAffiliatorAccountApi } from "../../utils/api/Account/Affiliator/getAffiliatorAccountApi";
import { updateAffiliatorAccountApi } from "../../utils/api/Account/Affiliator/updateAffiliatorAccountApi";

import styles from "../../styles/pages/Account/AccountPage.module.css";
import { Title } from "../../components/UI/Custom/Title";
import { LangContext } from "../../context/LangContext";

const translations = {
  ru: {
    title: "Мой аккаунт аффилиата",
  },
  en: {
    title: "My Affiliate Account",
  },
};

export const AccountAffiliatorPage = () => {
  const { langSelected } = useContext(LangContext);
  const t = translations[langSelected] || translations.ru;
  const [affiliatorInfo, setAffiliatorInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editableAffiliatorInfo, setEditableAffiliatorInfo] = useState(null);
  const [focusedField, setFocusedField] = useState(null);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  const accountFields = [
    { key: "id", label: "ID", editable: false },
    { key: "first_name", label: "Имя", editable: true },
    { key: "last_name", label: "Фамилия", editable: true },
    { key: "email", label: "Email", editable: true },
    { key: "affiliate", label: "Аффилиат", editable: false },
    { key: "created_at", label: "Дата создания", editable: false },
  ];

  const getAccountValueByKey = (data, key) => {
    return key.split(".").reduce((o, i) => (o ? o[i] : null), data);
  };

  const loadAffiliatorData = useCallback(async () => {
    setIsLoading(true);
    setErrors({});
    setTouched({});

    try {
      const data = await getAffiliatorAccountApi();
      setAffiliatorInfo(data);
      setEditableAffiliatorInfo(data);
    } catch (error) {
      handleError(error.response?.data || error, setErrors);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAffiliatorData();
  }, [loadAffiliatorData]);

  const handleCopyable = (label, value) => {
    if (typeof navigator === "undefined" || !value) return;

    navigator.clipboard?.writeText(String(value));
  };

  const handleFieldChange = useCallback((name, value) => {
    setEditableAffiliatorInfo((prev) => ({ ...prev, [name]: value }));
    setTouched((prev) => ({ ...prev, [name]: true }));
    setErrors((prev) => {
      const newErrors = { ...prev };
      if (newErrors[name]) {
        delete newErrors[name];
      }
      if (newErrors.general) {
        delete newErrors.general;
      }
      return newErrors;
    });
  }, []);

  const handleFocus = useCallback((name) => {
    setFocusedField(name);
    setTouched((prev) => ({ ...prev, [name]: true }));
    setErrors((prev) => {
      const newErrors = { ...prev };
      if (newErrors[name]) {
        delete newErrors[name];
      }
      if (newErrors.general) {
        delete newErrors.general;
      }
      return newErrors;
    });
  }, []);

  const handleBlur = useCallback((name) => {
    setFocusedField(null);
    setTouched((prev) => ({ ...prev, [name]: true }));
  }, []);

  const handleSaveClick = async () => {
    setErrors({});
    setIsLoading(true);

    try {
      const dataToUpdate = {
        first_name: editableAffiliatorInfo.first_name,
        last_name: editableAffiliatorInfo.last_name,
        email: editableAffiliatorInfo.email,
        affiliate: editableAffiliatorInfo.affiliate,
      };

      await updateAffiliatorAccountApi(dataToUpdate);
      await loadAffiliatorData();
      setIsEditing(false);
    } catch (error) {
      handleError(error.response?.data || error, setErrors);
      const newTouched = {};
      accountFields.forEach((field) => {
        if (field.editable) {
          newTouched[field.key] = true;
        }
      });
      setTouched((prev) => ({ ...prev, ...newTouched }));
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditableAffiliatorInfo(affiliatorInfo);
    setErrors({});
    setTouched({});
  };

  const renderField = (fieldConfig) => {
    const { key, label, editable } = fieldConfig;
    const value = getAccountValueByKey(affiliatorInfo, key);
    const editableValue = getAccountValueByKey(editableAffiliatorInfo, key);
    const hasError = !!errors[key] && !!touched[key];
    const errorMessage = errors[key];

    return (
      <div className={styles.infoItem} key={key}>
        <div className={styles.labelBox}>{label}:</div>
        <div className={styles.displayBox}>
          {isEditing && editable ? (
            <InputSelector
              filterName={key}
              selectedValue={editableValue}
              handleFilterChange={handleFieldChange}
              hasError={hasError}
              errorMessage={errorMessage}
              isFocused={focusedField === key}
              onFocus={handleFocus}
              onBlur={handleBlur}
              type={key === "email" ? "email" : "text"}
            />
          ) : (
            <span className={styles.displayValue}>
              {value || "Н/Д"}
            </span>
          )}
        </div>
        <div className={styles.actionBox}>
          {editable && !isEditing && (
            <div
              className={`${styles.icon} ${styles.editIcon}`}
              onClick={() => setIsEditing(true)}
              title="Редактировать"
            >
              <EditIcon />
            </div>
          )}
          <div
            className={`${styles.icon} ${styles.copyIcon}`}
            onClick={() => handleCopyable(label, value)}
            title="Копировать"
          >
            <ContentCopyIcon />
          </div>
        </div>
      </div>
    );
  };

  if (isLoading) {
    return <LoadingCircle size={30} thickness={1} />;
  }

  if (!affiliatorInfo && Object.keys(errors).length > 0) {
    return (
      <div className={`${styles.error} ${styles.fullPageError}`}>
        {errors.general || "Ошибка загрузки данных аккаунта. Пожалуйста, попробуйте снова."}
      </div>
    );
  }

  if (!affiliatorInfo) {
    return (
      <div className={styles.noData}>Данные аккаунта аффилиата не найдены.</div>
    );
  }

  return (
    <>
      <div className={styles.accountPage}>
        <Title h2={t.title} activeSpan={false} />
        {errors.general && <Notification message={errors.general} type={"error"} />}
        <div className={styles.accountContainer}>
          <div className={styles.infoGrid}>
            {accountFields.map((field) => renderField(field))}
          </div>

          <div className={styles.editActions}>
            {isEditing ? (
              <>
                <button
                  className={styles.saveButton}
                  onClick={handleSaveClick}
                  disabled={isLoading}
                >
                  <CheckIcon /> Сохранить
                </button>
                <button
                  className={styles.cancelButton}
                  onClick={handleCancelEdit}
                  disabled={isLoading}
                >
                  <CloseIcon /> Отмена
                </button>
              </>
            ) : (
              <button
                className={styles.editButton}
                onClick={() => setIsEditing(true)}
                disabled={isLoading}
              >
                <EditIcon /> Редактировать аккаунт
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
};
